import {useAuthStore} from '@stores/auth';
import {useSettingsStore} from '@stores/settings';
import ApiService from '@services/ApiService';
import { useAlert } from "@stores/alert";

interface WithdrawState {
    authStore: any;
    settingsStore: any;
    settings: any;
    image: string;
    link: boolean;
    type: string;
    amount: string;
    pix: string | null;
    loading: boolean;
    isWithdrawBonusConfirmationOpen: boolean;
    form: {
        message: {
            type: string;
            text: string;
        },
        error: string;
    }
    money: {
        decimal: string;
        thousands: string;
        prefix: string;
        suffix: string;
        precision: number;
        masked: boolean;
    };
}

export function useWithdraw() {
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();
    const { showAlert } = useAlert();
    const config = useRuntimeConfig();

    /**
     * Get the rollover amount
     */
    const rolloverAmount = computed(() => {
        const user: any = authStore.user;

        if (! user?.deposit_rollover) {
            return 0;
        }

        const amount = user.deposit_rollover.total - user.deposit_rollover.amount;

        if (amount <= 0) {
            return 0;
        }

        return number_format(amount / 100, 2, ",", ".");
    });

    /**
     * Check if the user need to accumplish rollover
     */
    const needRollover = computed(() => {
        const user: any = authStore.user;

        if (! user) {
            return false;
        }

        if (! user?.deposit_rollover) {
            return false;
        }

        const amount = user.deposit_rollover.total - user.deposit_rollover.amount;

        if (amount <= 0) {
            return false;
        }

        return true;
    });

    const state = reactive<WithdrawState>({
        authStore,
        settingsStore,
        settings: settingsStore.settings,
        image: "",
        link: false,
        type: "CPF",
        amount: "",
        pix: null,
        loading: false,
        isWithdrawBonusConfirmationOpen: false,
        form: {
            message: {
                type: "",
                text: "",
            },
            error: ""
        },
        money: {
            decimal: ",",
            thousands: ".",
            prefix: "R$ ",
            suffix: "",
            precision: 2,
            masked: false,
        },
    });

    onMounted(async () => {
        state.pix = state.authStore.user?.document?.number;
    });

    const dynamicMask = computed(() => {
        if (state.type === "TELEFONE") {
            state.pix = state.authStore.state.user.phone;
            return state.authStore.state.user.phone <= 14
                ? "(##) ####-####"
                : "(##) #####-####";
        }
        if (state.type === "CPF") {
            state.pix = state.authStore.state.user?.document?.number;
            return "###.###.###-##";
        }
        if (state.type === "EMAIL") {
            state.pix = state.authStore.state.user.email;
            return "";
        }
    });

    const toggleWithdrawBonusConfirmation = () => {
        state.isWithdrawBonusConfirmationOpen = !state.isWithdrawBonusConfirmationOpen;
    };

    const post = async () => {

        // Opening confirmation modal if user has active bonus
        if (Object.keys(authStore.activeRollover).length !== 0 && !state.isWithdrawBonusConfirmationOpen) {
            toggleWithdrawBonusConfirmation();
            return;
        }

        // Close confirmation modal if open
        if(state.isWithdrawBonusConfirmationOpen) toggleWithdrawBonusConfirmation();
        
        // Ask for user document if it's not filled
        if (!state.authStore.user?.document?.number) {
            state.authStore.toggleUserDocumentModal();
            return;
        }

        state.link = false;
        if (!state.amount) {
            state.form.error = "Campo obrigatorio";
            return;
        }

        state.loading = true;
        let cleanedString = state.amount.replace("R$ ", "").split('.').join('');
        let amountInt = cleanedString.replace(',', '')

        let {data} = await ApiService.post("/api/withdraws", {
            amount: Number(amountInt),
            pixType: state.type,
        });

        state.loading = false;

        if (!data.success) {
            state.form.error = data.message;
            state.link = data.link;
        }

        if (data.data?.message === 'Unauthenticated.') {
            showAlert("error", "Sessão expirada, faça login novamente.");
            authStore.setLoggedIn(false);
            return
        }

        if (data.success) {
            if (config.public.APP_THEME === "orange") {
                state.form.message.text = data.message;
                state.form.message.type = "info";
            } else {
               showAlert("success", data.message);    
               navigateTo("/user/wallet");
            }
            await authStore.getUser();
        }
    };

    return {
        state,
        dynamicMask,
        toggleWithdrawBonusConfirmation,
        post,
        needRollover,
        rolloverAmount,
    };
}